import { useEffect, useState } from "react";
import { adminListLanguages, adminTranslateContent, adminSaveTranslation, adminPublishTranslation } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, Globe, ChevronDown } from "lucide-react";

const STR_LABELS = {
  title: "Title", description: "Short description", intro: "Intro", body: "Overview / Body",
  meaning: "Meaning", context: "Context", usage: "How it is used", sankalpa: "Sankalpa",
  source: "Source", editorial_note: "Editorial note",
};
const SKIP = new Set(["status", "updated_at"]);

function Area({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-3">{label}</span>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={value && value.length > 90 ? 3 : 1}
        className="w-full resize-y border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
    </label>
  );
}

function TranslationEditor({ itemId, code, label, tr, setTr }) {
  const set = (k, v) => setTr({ ...tr, [k]: v });
  const setArr = (k, i, field, v) => {
    const arr = [...(tr[k] || [])];
    arr[i] = field ? { ...arr[i], [field]: v } : v;
    setTr({ ...tr, [k]: arr });
  };

  const stringKeys = Object.keys(tr).filter((k) => !SKIP.has(k) && typeof tr[k] === "string");
  return (
    <div className="mt-4 space-y-3 border-t border-line pt-4">
      {stringKeys.map((k) => (
        <Area key={k} label={STR_LABELS[k] || k} value={tr[k]} onChange={(v) => set(k, v)} />
      ))}
      {Array.isArray(tr.materials) && tr.materials.map((m, i) => (
        <Area key={`mat-${i}`} label={`Material ${i + 1}`} value={m} onChange={(v) => setArr("materials", i, null, v)} />
      ))}
      {Array.isArray(tr.chapters) && tr.chapters.map((c, i) => (
        <div key={`ch-${i}`} className="rounded border border-line/60 p-3">
          <p className="mb-2 overline-label text-gold">Chapter {i + 1}</p>
          <Area label="Chapter title" value={c.title} onChange={(v) => setArr("chapters", i, "title", v)} />
          <Area label="Chapter body" value={c.body} onChange={(v) => setArr("chapters", i, "body", v)} />
        </div>
      ))}
      {Array.isArray(tr.steps) && tr.steps.map((s, i) => (
        <div key={`st-${i}`} className="rounded border border-line/60 p-3">
          <p className="mb-2 overline-label text-gold">Step {i + 1}</p>
          <Area label="Step title" value={s.title} onChange={(v) => setArr("steps", i, "title", v)} />
          <Area label="Step text" value={s.text} onChange={(v) => setArr("steps", i, "text", v)} />
        </div>
      ))}
      {Array.isArray(tr.mantras) && tr.mantras.map((m, i) => (
        <Area key={`mn-${i}`} label={`Mantra ${i + 1} meaning`} value={m.meaning} onChange={(v) => setArr("mantras", i, "meaning", v)} />
      ))}
    </div>
  );
}

export default function ContentTranslations({ itemId, initialI18n = {} }) {
  const [langs, setLangs] = useState([]);
  const [i18n, setI18n] = useState(initialI18n || {});
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    adminListLanguages().then((all) => setLangs(all.filter((l) => !l.system))).catch(() => {});
  }, []);

  if (!itemId) return null;

  const autoTranslate = async (code) => {
    setBusy(code);
    try {
      const tr = await adminTranslateContent(itemId, code);
      setI18n({ ...i18n, [code]: tr });
      setOpen(code);
      toast.success("Draft translation ready — review and publish below.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Translation failed."); }
    finally { setBusy(""); }
  };
  const save = async (code) => {
    await adminSaveTranslation(itemId, code, i18n[code]);
    toast.success("Translation saved");
  };
  const publish = async (code, pub) => {
    const r = await adminPublishTranslation(itemId, code, pub);
    setI18n({ ...i18n, [code]: { ...i18n[code], status: r.status } });
    toast.success(pub ? "Published — now visible to readers in this language" : "Unpublished");
  };

  return (
    <div className="mt-8 border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.05)] p-5 sm:p-6" data-testid="content-translations">
      <div className="mb-1 flex items-center gap-2">
        <Globe className="h-4 w-4 text-gold" />
        <h3 className="font-serif-latin text-lg text-ink">Translations</h3>
      </div>
      <p className="mb-4 text-xs text-ink-3">Auto-translate this entry into other languages with AI, review the draft, then publish. Save the entry first if you just made edits.</p>
      {langs.length === 0 && <p className="text-sm text-ink-3">No extra languages yet. Add one under Admin → Languages.</p>}
      <div className="space-y-2">
        {langs.map((l) => {
          const tr = i18n[l.code];
          const status = tr?.status;
          return (
            <div key={l.code} data-testid={`tr-${l.code}`} className="border border-line bg-surface">
              <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-2">
                  <span className="font-serif-latin text-ink">{l.native_label || l.label}</span>
                  <span className="text-xs text-ink-3">· {l.code}</span>
                  {status === "published" && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-green-700"><Check className="h-3 w-3" />published</span>}
                  {status === "draft" && <span className="text-[10px] uppercase tracking-wide text-gold">draft</span>}
                  {!status && <span className="text-[10px] uppercase tracking-wide text-ink-3">not translated</span>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button data-testid={`tr-auto-${l.code}`} onClick={() => autoTranslate(l.code)} disabled={busy === l.code} className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-50">
                    {busy === l.code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {status ? "Re-translate" : "Auto-translate"}
                  </button>
                  {tr && (
                    <button data-testid={`tr-review-${l.code}`} onClick={() => setOpen(open === l.code ? null : l.code)} className="inline-flex items-center gap-1 border border-line px-3 py-1.5 text-xs text-ink-2 transition-colors hover:text-ink">
                      Review <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === l.code ? "rotate-180" : ""}`} />
                    </button>
                  )}
                  {tr && (status !== "published"
                    ? <button data-testid={`tr-publish-${l.code}`} onClick={() => publish(l.code, true)} className="bg-[var(--saffron)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">Publish</button>
                    : <button data-testid={`tr-unpublish-${l.code}`} onClick={() => publish(l.code, false)} className="border border-line px-3 py-1.5 text-xs text-ink-2 hover:text-ink">Unpublish</button>)}
                </div>
              </div>
              {tr && open === l.code && (
                <div className="border-t border-line p-4">
                  <TranslationEditor itemId={itemId} code={l.code} label={l.label} tr={tr} setTr={(v) => setI18n({ ...i18n, [l.code]: v })} />
                  <button data-testid={`tr-save-${l.code}`} onClick={() => save(l.code)} className="mt-4 border border-line px-4 py-2 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">Save changes</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
